"use client";

import type { ReactNode } from "react";
import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { ComparisonStrings } from "@/components/tools/correlation-comparison-calculator";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import { t } from "@/lib/i18n/interpolate";
import { normalQuantile } from "@/lib/stats/distributions";
import {
  formatPRelation,
  formatPValue,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  type Alternative,
  compareDependent,
  compareIndependent,
  compareSeveral,
  fisherZ,
  type Interval,
  singleCorrelation,
  validR,
} from "@/lib/tool-kit/stats-regression/correlation-tests";
import { apaBounded, apaPClause } from "@/lib/tool-kit/stats-research/format";

/*
 * The four result panels of the correlation-comparison widget. Each takes the parsed
 * inputs, computes with `correlation-tests.ts`, and renders cards, sentences, an APA line
 * and the working. Every word comes from the widget's strings.
 */

interface CommonProps {
  fmt: NumberFormatter;
  strings: ComparisonStrings;
  level: number;
  levelText: string;
  alternative: Alternative;
}

function Result({
  strings,
  cards,
  sentences,
  apa,
  working,
}: {
  strings: ComparisonStrings;
  cards: ReactNode;
  sentences: readonly string[];
  apa: string;
  working: ReactNode;
}) {
  return (
    <section className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <div aria-live="polite" className="space-y-4">
        <StatGrid>{cards}</StatGrid>
        {sentences.map((sentence) => (
          <Explanation key={sentence}>{sentence}</Explanation>
        ))}
        <div className="flex flex-wrap items-center gap-3">
          <p className="font-mono text-foreground text-sm">
            <span className="text-muted-foreground">{strings.apaLabel}: </span>
            {apa}
          </p>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyResult}
            small
            text={apa}
          />
        </div>
      </div>
      <WorkingPanel title={strings.workingTitle}>{working}</WorkingPanel>
    </section>
  );
}

function intervalText(
  ci: Interval,
  fmt: NumberFormatter,
  template = "{low} to {high}"
): string {
  return t(template, {
    low: formatStat(fmt, ci.low),
    high: formatStat(fmt, ci.high),
  });
}

function significance(
  p: number,
  fmt: NumberFormatter,
  strings: ComparisonStrings
): string {
  return t(p < 0.05 ? strings.significant : strings.notSignificant, {
    p: formatPRelation(fmt, p),
  });
}

export function SinglePanel({
  r,
  n,
  rho0,
  fmt,
  strings,
  level,
  levelText,
  alternative,
}: CommonProps & { r: number; n: number; rho0: number | null }) {
  const report = singleCorrelation(r, n, level, rho0, alternative);
  if (!report) {
    return <Notice>{strings.enterPrompt}</Notice>;
  }
  const num = (value: number) => formatStat(fmt, value);
  const crit = num(normalQuantile(1 - (1 - level) / 2));
  const sentences = [
    t(strings.singleSummary, {
      r: num(r),
      n: fmt(n),
      low: num(report.ci.low),
      high: num(report.ci.high),
      level: levelText,
    }),
  ];
  if (report.test) {
    sentences.push(
      `${t(strings.singleTestSummary, {
        rho0: num(report.test.rho0),
        z: num(report.test.z),
        p: formatPRelation(fmt, report.test.p),
      })} ${significance(report.test.p, fmt, strings)}`
    );
  }
  const apa = t(strings.singleApa, {
    df: fmt(n - 2),
    r: apaBounded(fmt, r, 2),
    level: levelText,
    low: apaBounded(fmt, report.ci.low, 2),
    high: apaBounded(fmt, report.ci.high, 2),
  });
  return (
    <Result
      apa={apa}
      cards={
        <>
          <StatCard
            emphasis
            label={t(strings.singleCi, { level: levelText })}
            value={intervalText(report.ci, fmt)}
          />
          <StatCard label={strings.singleFisherZ} value={num(report.z)} />
          <StatCard
            label={strings.singleSe}
            value={num(report.standardError)}
          />
          <StatCard label={strings.singleDf} value={fmt(n - 2)} />
          {report.test ? (
            <>
              <StatCard
                label={strings.singleTestZ}
                value={num(report.test.z)}
              />
              <StatCard
                label={strings.singleTestP}
                value={formatPValue(fmt, report.test.p)}
              />
            </>
          ) : null}
        </>
      }
      sentences={sentences}
      strings={strings}
      working={
        <>
          <Formula>
            {t(strings.singleWorking, {
              z: num(report.z),
              m: fmt(n - 3),
              se: num(report.standardError),
              crit,
              low: num(report.ci.low),
              high: num(report.ci.high),
            })}
          </Formula>
          {report.test ? (
            <Formula>
              {t(strings.singleTestWorking, {
                z: num(report.z),
                z0: num(fisherZ(report.test.rho0)),
                se: num(report.standardError),
                stat: num(report.test.z),
              })}
            </Formula>
          ) : null}
        </>
      }
    />
  );
}

export function IndependentPanel({
  r1,
  n1,
  r2,
  n2,
  fmt,
  strings,
  level,
  levelText,
  alternative,
}: CommonProps & { r1: number; n1: number; r2: number; n2: number }) {
  const report = compareIndependent(r1, n1, r2, n2, level, alternative);
  if (!report) {
    return <Notice>{strings.enterPrompt}</Notice>;
  }
  const num = (value: number) => formatStat(fmt, value);
  const apa = t(strings.indApa, {
    r1: apaBounded(fmt, r1, 2),
    n1: fmt(n1),
    r2: apaBounded(fmt, r2, 2),
    n2: fmt(n2),
    z: num(report.z),
    p: apaPClause(fmt, report.p),
  });
  return (
    <Result
      apa={apa}
      cards={
        <>
          <StatCard emphasis label={strings.indZ} value={num(report.z)} />
          <StatCard label={strings.indP} value={formatPValue(fmt, report.p)} />
          <StatCard label={strings.indDiff} value={num(report.difference)} />
          <StatCard
            label={t(strings.indDiffCi, { level: levelText })}
            value={intervalText(report.differenceCi, fmt)}
          />
          <StatCard
            label={t(strings.indCi1, { level: levelText })}
            value={intervalText(report.ci1, fmt)}
          />
          <StatCard
            label={t(strings.indCi2, { level: levelText })}
            value={intervalText(report.ci2, fmt)}
          />
        </>
      }
      sentences={[
        report.p < 0.05 ? strings.indSummaryDifferent : strings.indSummarySame,
        significance(report.p, fmt, strings),
      ]}
      strings={strings}
      working={
        <>
          <Formula>
            {t(strings.indWorking, {
              z1: num(report.z1),
              z2: num(report.z2),
              m1: fmt(n1 - 3),
              m2: fmt(n2 - 3),
              se: num(report.standardError),
              z: num(report.z),
            })}
          </Formula>
          <Explanation>{strings.indZouNote}</Explanation>
        </>
      }
    />
  );
}

export function DependentPanel({
  rjk,
  rjh,
  rkh,
  n,
  fmt,
  strings,
  alternative,
}: CommonProps & { rjk: number; rjh: number; rkh: number; n: number }) {
  const report = compareDependent(rjk, rjh, rkh, n, alternative);
  if (!report) {
    const allValid = [rjk, rjh, rkh].every(validR) && n > 3;
    return (
      <Notice>
        {allValid ? strings.impossibleMatrix : strings.enterPrompt}
      </Notice>
    );
  }
  const num = (value: number) => formatStat(fmt, value);
  const mean = (rjk + rjh) / 2;
  const cov = report.steiger.covariance;
  const apa = t(strings.depApa, {
    z: num(report.steiger.z),
    p: apaPClause(fmt, report.steiger.p),
    rjk: apaBounded(fmt, rjk, 2),
    rjh: apaBounded(fmt, rjh, 2),
    rkh: apaBounded(fmt, rkh, 2),
    n: fmt(n),
  });
  return (
    <Result
      apa={apa}
      cards={
        <>
          <StatCard
            emphasis
            label={strings.depSteiger}
            value={num(report.steiger.z)}
          />
          <StatCard
            label={strings.depSteigerP}
            value={formatPValue(fmt, report.steiger.p)}
          />
          <StatCard label={strings.depDiff} value={num(report.difference)} />
          <StatCard
            label={strings.depWilliams}
            value={num(report.williams.t)}
          />
          <StatCard
            label={strings.depWilliamsP}
            value={formatPValue(fmt, report.williams.p)}
          />
          <StatCard label={strings.depDf} value={fmt(report.df)} />
          <StatCard
            label={strings.depHotelling}
            value={num(report.hotelling.t)}
          />
          <StatCard
            label={strings.depHotellingP}
            value={formatPValue(fmt, report.hotelling.p)}
          />
        </>
      }
      sentences={[
        report.steiger.p < 0.05
          ? strings.depSummaryDifferent
          : strings.depSummarySame,
        t(strings.depSummary, {
          z: num(report.steiger.z),
          p: formatPRelation(fmt, report.steiger.p),
          pw: formatPRelation(fmt, report.williams.p),
          ph: formatPRelation(fmt, report.hotelling.p),
        }),
      ]}
      strings={strings}
      working={
        <>
          <Formula>
            {t(strings.depWorking, {
              mean: num(mean),
              psi: num(cov * (1 - mean * mean) ** 2),
              c: num(cov),
            })}
          </Formula>
          <Formula>
            {t(strings.depWorking2, {
              z1: num(fisherZ(rjk)),
              z2: num(fisherZ(rjh)),
              m: fmt(n - 3),
              denominator: num(2 - 2 * cov),
              z: num(report.steiger.z),
            })}
          </Formula>
          <Explanation>{strings.depWilliamsNote}</Explanation>
        </>
      }
    />
  );
}

export function SeveralPanel({
  rs,
  ns,
  fmt,
  strings,
  level,
  levelText,
}: CommonProps & { rs: number[]; ns: number[] }) {
  const report = compareSeveral(rs, ns, level);
  if (!report) {
    return (
      <Notice>{rs.length < 2 ? strings.sevNeed : strings.enterPrompt}</Notice>
    );
  }
  const num = (value: number) => formatStat(fmt, value);
  const apa = t(strings.sevApa, {
    df: fmt(report.df),
    q: num(report.q),
    p: apaPClause(fmt, report.p),
    i2: fmt(report.i2, { style: "percent", maximumFractionDigits: 0 }),
    pooled: apaBounded(fmt, report.pooled, 2),
    level: levelText,
    low: apaBounded(fmt, report.pooledCi.low, 2),
    high: apaBounded(fmt, report.pooledCi.high, 2),
  });
  const zBar = Math.atanh(report.pooled);
  return (
    <Result
      apa={apa}
      cards={
        <>
          <StatCard emphasis label={strings.sevQ} value={num(report.q)} />
          <StatCard label={strings.sevDf} value={fmt(report.df)} />
          <StatCard label={strings.sevP} value={formatPValue(fmt, report.p)} />
          <StatCard
            label={strings.sevI2}
            value={fmt(report.i2, {
              style: "percent",
              maximumFractionDigits: 0,
            })}
          />
          <StatCard label={strings.sevPooled} value={num(report.pooled)} />
          <StatCard
            label={t(strings.sevPooledCi, { level: levelText })}
            value={intervalText(report.pooledCi, fmt)}
          />
        </>
      }
      sentences={[
        report.p < 0.05 ? strings.sevSummaryDifferent : strings.sevSummarySame,
        significance(report.p, fmt, strings),
      ]}
      strings={strings}
      working={
        <>
          <Formula>
            {t(strings.sevWorking, {
              zbar: num(zBar),
              q: num(report.q),
              df: fmt(report.df),
              pooled: num(report.pooled),
            })}
          </Formula>
          <h3 className={WIDGET_H3}>{strings.sevTable}</h3>
          <ResultTable
            head={[
              strings.colStudy,
              strings.colR,
              strings.colN,
              strings.colZ,
              strings.colWeight,
            ]}
            rows={rs.map((r, i) => [
              t(strings.studyLabel, { number: fmt(i + 1) }),
              num(r),
              fmt(ns[i]),
              num(fisherZ(r)),
              fmt(report.weights[i], {
                style: "percent",
                maximumFractionDigits: 1,
              }),
            ])}
            wrapFirstColumn
          />
        </>
      }
    />
  );
}
